import { routes } from "../../lib/nav";
import { SITE_URL } from "../../lib/site";
import type { CaseStudy } from "./types";

const dir = "/cases/timebit";

const img = (file: string, alt: string, span: 2 | 3 | 6 = 6) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  wide: span === 6,
  span,
});

const mobile = (file: string, alt: string) => ({
  src: `${dir}/${file}.webp`,
  alt,
  width: 1920,
  height: 1200,
  span: 3 as const,
});

export const timebit: CaseStudy = {
  slug: "timebit",
  title: "TimeBit",
  cardTitle: "Разработка интернет-магазина часов TimeBit",
  subtitle: "Интернет-магазин наручных часов",
  industry: { label: "Ритейл и аксессуары", slug: "retail" },
  eyebrow: "Интернет-магазин · Часы и аксессуары",
  h1: "Разработка интернет-магазина часов TimeBit",
  lead:
    "Разработали интернет-магазин TimeBit с большим каталогом часов: фильтрами по характеристикам и брендам, сравнением моделей, клубными ценами, бонусами и отслеживанием заказа.",
  heroButtons: { site: "Смотреть сайт", discuss: "Обсудить интернет-магазин" },

  seo: {
    primaryKeyword: "разработка интернет-магазина часов",
    secondaryKeywords: [
      "создание интернет-магазина часов",
      "e-commerce для часового магазина",
      "сайт магазина наручных часов",
      "разработка магазина с большим каталогом",
    ],
    semanticEntities: [
      "каталог часов",
      "фильтры по характеристикам",
      "бренды",
      "сравнение моделей",
      "карточка товара",
      "клубные цены",
      "бонусы",
      "сертификаты",
      "кредитование",
      "выбор города",
      "отслеживание заказа",
    ],
    excludedKeywords: [
      "купить часы",
      "наручные часы Москва",
      "швейцарские часы цена",
      "мужские часы",
    ],
    title: "Интернет-магазин TimeBit — кейс HAY.agency",
    description:
      "Кейс HAY.agency: интернет-магазин TimeBit с большим каталогом, фильтрами по характеристикам, брендами, сравнением, клубными ценами и отслеживанием заказа.",
    h1: "Разработка интернет-магазина часов TimeBit",
    slug: "timebit",
    canonicalUrl: `${SITE_URL}/cases/timebit`,
    ogTitle: "Интернет-магазин часов TimeBit",
    ogDescription:
      "Большой каталог, фильтры, сравнение моделей, клубные цены и отслеживание заказа для магазина наручных часов.",
    ogImage: `${SITE_URL}${dir}/timebit-og.webp`,
    robotsIndex: true,
    datePublished: "2023-09-01",
    dateModified: "2026-09-01",
  },

  links: {
    relatedService: {
      label: "Разработка и развитие интернет-магазинов",
      to: routes.ecom,
      note: "Каталог, карточка товара и сравнение",
    },
    relatedIndustry: {
      label: "SEO-продвижение интернет-магазинов",
      to: routes.seo,
      note: "Видимость каталога и карточек товара",
    },
    relatedCases: [
      {
        label: "Интернет-магазин профессиональной косметики Quantum Shop",
        to: "/cases/quantum-shop",
        note: "Большой каталог и карточка товара",
      },
      {
        label: "Разработка e-commerce платформы для производителя косметики Levrana",
        to: "/cases/levrana",
        note: "Мультибрендовый каталог и лояльность",
      },
    ],
    relatedArticles: [],
  },

  cta: {
    title: "Нужен интернет-магазин со сложным каталогом?",
    text: "Спроектируем фильтры, сравнение и карточку товара, соберём магазин и возьмём его на развитие.",
    button: "Обсудить интернет-магазин",
  },

  client: {
    name: "TimeBit",
    url: "https://timebit.ru/",
    city: "Россия",
    industry: "Часы и аксессуары",
  },
  year: "2023",
  services: [
    "Дизайн интернет-магазина",
    "Разработка каталога и фильтров",
    "Карточка товара и сравнение",
    "Клубные цены и бонусы",
    "Отслеживание заказа",
  ],
  platform: "1С-Битрикс",
  tags: [
    { label: "Интернет-магазин", to: routes.ecom },
    { label: "Разработка", to: routes.ecom },
  ],
  facts: [
    { label: "Клиент", value: "TimeBit" },
    { label: "Отрасль", value: "Часы и аксессуары" },
    { label: "Тип проекта", value: "Интернет-магазин со сложным каталогом" },
    { label: "Платформа", value: "1С-Битрикс" },
  ],

  results: {
    title: "Что реализовали",
    items: [
      "Каталог с фильтрами по типу, стилю, водозащите, материалу, стране, функциям и бренду",
      "Страницы официального партнёрства с представленными марками",
      "Сравнение моделей и заполненная корзина",
      "Клубные цены, бонусы и сертификаты",
      "Кредитование при оформлении заказа",
      "Выбор города и отслеживание заказа",
    ],
  },

  story: [
    {
      title: "Задача",
      paragraphs: [
        "Магазину часов нужен был каталог, который выдерживает большой набор характеристик — тип, стиль, водозащита, материал, страна, функции и бренд — и при этом не превращает подбор модели в перегруженный фильтр.",
        "Вторая задача — довести покупателя от сравнения моделей до оформления заказа с клубными ценами, бонусами и корректной работой по регионам.",
      ],
    },
  ],
  features: [],

  design: {
    title: "Дизайн: тёплый графит вместо белого каталога",
    paragraphs: [
      "Интерфейс построен на тёплом графитовом фоне — крупные фотографии часов и карточки моделей читаются на нём лучше, чем на чистом белом.",
      "Сравнение и карточка товара показаны в заполненном виде, чтобы сразу передать логику выбора между несколькими моделями.",
    ],
    bullets: [
      "Тёплый графит/серый фон вместо белого",
      "Крупные фотографии часов в каталоге",
      "Заполненные сравнение и корзина",
    ],
  },

  identity: {
    fonts: [
      { name: "Futura PT", role: "Заголовки", sample: "Каталог часов" },
      { name: "Futura PT", role: "Интерфейс и характеристики", sample: "Сравнение моделей" },
    ],
    colors: [
      { hex: "#2A2A2E", name: "Фон каталога" },
      { hex: "#C9A25A", name: "Акцент бренда" },
      { hex: "#F5F5F3", name: "Текст на тёмном фоне" },
    ],
  },

  cover: img("timebit-case-cover", "Главная страница интернет-магазина часов TimeBit", 6),

  gallery: [
    img("timebit-catalog-filters", "Каталог и фильтры интернет-магазина TimeBit", 6),
  ],

  responsive: {
    text: [
      "Мобильная версия сохраняет логику выбора: фильтры, карточка товара и сравнение доступны в одном сценарии без потери характеристик.",
      "Состояния сравнения и корзины в мобильной версии показаны заполненными.",
    ],
    images: [
      mobile("timebit-mobile-hero", "Мобильная главная интернет-магазина TimeBit"),
      mobile("timebit-mobile-order", "Мобильное отслеживание заказа в TimeBit"),
    ],
  },

  columnBlocks: [
    {
      id: "catalog",
      title: "Сложный каталог",
      paragraphs: [
        "Каталог фильтруется по типу, стилю, водозащите, материалу, стране, функциям и бренду. Такой набор фильтров позволяет покупателю быстро сузить выбор среди большого ассортимента моделей.",
      ],
      columns: [
        { title: "Характеристики", items: ["Тип", "Стиль", "Водозащита", "Материал"] },
        { title: "Дополнительно", items: ["Страна", "Функции", "Бренд"] },
      ],
      images: [
        img("timebit-catalog-filters", "Фильтры каталога интернет-магазина TimeBit", 6),
      ],
    },
    {
      id: "comparison",
      title: "Сравнение и карточка",
      paragraphs: [
        "Сравнение нескольких моделей и карточка товара с корзиной помогают покупателю принять решение без консультации по телефону.",
      ],
      bullets: [
        "Сравнение 2–3 моделей",
        "Карточка товара с характеристиками",
        "Корзина и оформление заказа",
      ],
      images: [
        img("timebit-comparison-card", "Сравнение моделей и карточка товара TimeBit", 6),
      ],
    },
    {
      id: "club",
      title: "Клубные механики",
      paragraphs: [
        "Клубные цены, бонусы и сертификаты объединены в единый блок мотивации к покупке, а кредитование доступно прямо при оформлении заказа.",
      ],
      bullets: ["Клубные цены", "Бонусы и сертификаты", "Кредитование"],
    },
    {
      id: "order",
      title: "Заказ по регионам",
      paragraphs: [
        "Выбор города, пункты выдачи и отслеживание заказа показаны в чистых состояниях, без открытых всплывающих окон.",
      ],
      bullets: ["Выбор города", "Пункты выдачи", "Отслеживание заказа"],
    },
  ],

  servicesDone: {
    title: "Комплекс работ HAY.agency",
    items: [{ label: "Разработка интернет-магазина", to: routes.ecom }],
  },
};
